export interface SubtitleTrack {
  /** ISO 639-1 code: the key of the API's `subtitles` map. */
  language: string;
  url: string;
  label: string;
}

interface SubtitledNode {
  subtitles?: Record<string, string> | null;
  subtitlesUrl?: string | null;
}

/** The language's own name, capitalised ("Español", "Deutsch"); the code when the browser has no name for it. */
export const subtitleLanguageLabel = (language: string): string => {
  try {
    const name = new Intl.DisplayNames([language], {type: 'language'}).of(
      language,
    );
    if (name && name.toLowerCase() !== language.toLowerCase()) {
      return name.charAt(0).toLocaleUpperCase(language) + name.slice(1);
    }
  } catch {
    // Not a language code the browser knows.
  }
  return language.toUpperCase();
};

/**
 * A video node's subtitle tracks from the API's `subtitles` map (signed URLs by ISO 639-1 code), the
 * original-language track (`subtitlesUrl`) first. APIs from before translated subtitles send only
 * `subtitlesUrl`, which is read as English.
 */
export const subtitleTracks = (node?: unknown): SubtitleTrack[] => {
  const {subtitles, subtitlesUrl} = (node ?? {}) as SubtitledNode;
  const entries: [string, string][] =
    subtitles && Object.keys(subtitles).length > 0
      ? Object.entries(subtitles)
      : subtitlesUrl
      ? [['en', subtitlesUrl]]
      : [];
  return entries
    .filter(([language, url]) => !!language && !!url)
    .map(([language, url]) => ({
      language,
      url,
      label: subtitleLanguageLabel(language),
    }))
    .sort(
      (a, b) =>
        Number(b.url === subtitlesUrl) - Number(a.url === subtitlesUrl) ||
        a.label.localeCompare(b.label),
    );
};

/** The track captions open on: the viewer's browser language when the video has it, else the original. */
export const preferredSubtitleLanguage = (
  tracks: SubtitleTrack[],
  preferred: string | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator.language,
): string | null => {
  const base = preferred?.split('-')[0].toLowerCase();
  return (
    tracks.find(track => track.language === base)?.language ??
    tracks[0]?.language ??
    null
  );
};

/** react-player's file tracks: one per language, so the browser loads each as its own text track. */
export const playerTracks = (tracks: SubtitleTrack[]) =>
  tracks.map(track => ({
    kind: 'subtitles',
    src: track.url,
    srcLang: track.language,
    label: track.label,
  }));

/**
 * react-player's file config for a video with these tracks. The signed WebVTT URLs are on the storage
 * or CDN origin, and a <track> loads with its video's CORS mode: without crossorigin the browser
 * refuses a cross-origin track and no caption ever shows. Videos without subtitles keep the plain
 * request.
 */
export const playerFileConfig = (tracks: SubtitleTrack[]) => ({
  attributes: tracks.length > 0 ? {crossOrigin: 'anonymous'} : {},
  tracks: playerTracks(tracks),
});

/** Shows the chosen language's text track and hides the others; null hides them all. */
export const showSubtitleTrack = (
  video: HTMLVideoElement | null | undefined,
  language: string | null,
) => {
  if (!video) return;
  for (let i = 0; i < video.textTracks.length; i++) {
    const track = video.textTracks[i];
    track.mode =
      language !== null && track.language === language ? 'showing' : 'disabled';
  }
};

/**
 * The language to show for the viewer's choice: the choice itself when this video has it, the
 * preferred track when captions are on but this video lacks it, and null when captions are off.
 */
export const resolveSubtitleLanguage = (
  tracks: SubtitleTrack[],
  chosen: string | null,
): string | null =>
  chosen === null
    ? null
    : tracks.some(track => track.language === chosen)
    ? chosen
    : preferredSubtitleLanguage(tracks);
